/*
 * Назначение: страница авторизации админ-панели.
 * Функционал: форма «логин + пароль», валидация, обработка 401/403/429,
 * после успешного входа возвращает пользователя на исходный URL (если он
 * пришёл с приватной страницы) либо на /dashboard.
 */

import { useState } from "react";
import { AlertCircle, Loader2, Sparkles } from "lucide-react";
import { useLocation, useNavigate } from "react-router";

import { useAuth } from "../components/auth/auth-provider";
import { ApiError } from "../../lib/api";
import type { LoginResult } from "../../lib/auth";
import { CodeForm } from "./code-form";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export function LoginPage() {
  const { login, confirmCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loginValue, setLoginValue] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Заполнено — пароль принят, ждём код с почты.
  const [challenge, setChallenge] = useState<LoginResult | null>(null);

  // Куда возвращать после входа — либо то, откуда пришли через RequireAuth, либо /dashboard
  const fromState = (location.state as { from?: Location } | null)?.from;
  const redirectTo =
    fromState && "pathname" in fromState
      ? `${fromState.pathname}${(fromState as Location).search ?? ""}`
      : "/dashboard";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const trimmed = loginValue.trim();
    if (!trimmed || !password) {
      setError("Заполни оба поля");
      return;
    }

    setLoading(true);
    try {
      const result = await login(trimmed, password);
      if (result.twofa_required && result.challenge_id) {
        // Сессии ещё нет: показываем ввод кода, пароль больше не держим.
        setChallenge(result);
        setPassword("");
        setLoading(false);
        return;
      }
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError("Неверный логин или пароль");
        } else if (err.status === 403) {
          setError("Пользователь заблокирован. Обратись к владельцу системы");
        } else if (err.status === 429) {
          setError("Слишком много попыток. Попробуй через 15 минут");
        } else {
          setError(err.detail || "Не удалось войти");
        }
      } else {
        setError("Сервер недоступен. Проверь соединение");
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-[-10%] left-[-10%] h-[500px] w-[500px] rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute bottom-[-15%] right-[-10%] h-[500px] w-[500px] rounded-full bg-info/15 blur-3xl" />
      </div>

      <Card className="w-full max-w-md shadow-xl border-border/60">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Sparkles className="h-6 w-6" />
          </div>
          <CardTitle className="text-2xl">ИИ-продавец</CardTitle>
          <CardDescription>
            {challenge ? "Подтверди вход кодом из письма" : "Войди в админ-панель"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {challenge && challenge.challenge_id ? (
            <CodeForm
              emailHint={challenge.email_hint}
              ttlMinutes={challenge.code_ttl_minutes}
              canRemember
              onConfirm={async (code, rememberDevice) => {
                await confirmCode(challenge.challenge_id as string, code, rememberDevice);
                navigate(redirectTo, { replace: true });
              }}
              onBack={() => {
                setChallenge(null);
                setError("");
              }}
            />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="login">Логин</Label>
              <Input
                id="login"
                type="text"
                placeholder="admin"
                value={loginValue}
                onChange={(e) => setLoginValue(e.target.value)}
                disabled={loading}
                autoComplete="username"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Пароль</Label>
              <Input
                id="password"
                type="password"
                placeholder="Введи пароль"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                autoComplete="current-password"
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Войти
            </Button>
          </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
